import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { combineLatest } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';
import SwiperCore, { EffectFade, Lazy, Navigation } from 'swiper/core';
import { LazyOptions } from 'swiper/types';
import {
    carImageFallbackIcon,
    checkmarkIcon,
    crossLightIcon,
    pinIcon,
} from '../../../icons';
import { CarDetailsService } from '../../car-details-service/car-details.service';
import { getSizedBilinfoImage } from '../../core/helpers';
import { SettingsService } from '../../core/settings.service';
import { Environment, ENVIRONMENT } from '../../environment/environment';
import { FilterService } from '../../filter-service/filter.service';
import {
    LeasingAgreementType,
    LeasingPurpose,
    LeasingService,
} from '../../leasing/leasing.service';
import { NavigationService } from '../../navigation/navigation.service';
import { FeatureDetectionService } from '../../utils/helpers/feature-detection.service';

SwiperCore.use([EffectFade, Navigation, Lazy]);

@Component({
    selector: 'app-product-item',
    templateUrl: './product-item.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductItemComponent implements OnInit, AfterViewInit, OnDestroy {
    private unlistenClicks: (() => void)[] = [];

    @Input() enableScrollTo? = false;
    @Input() isLeasingOnlyPage: boolean;
    @Input() isOnPLP = false;
    @Input() product: any;
    @Input() showCashPrice: boolean = false;
    @Input() showLeasingPrices: boolean | undefined | null;
    @Input() sliderImagesAmount?: number;
    @Input() sliderImageSize = 400;
    @ViewChild('componentRef') componentRef: ElementRef;

    altText: string;
    carModelDataToDecimal: number | string;
    checkmarkIcon = checkmarkIcon;
    crossLightIcon = crossLightIcon;
    currentImageIndex: number;
    carImageFallbackIcon = carImageFallbackIcon;
    isBrowser = this.featureDetectionService.isBrowser();
    isMotorpoint = this.environment.site === 'Motorpoint';
    pinIcon = pinIcon;
    resizedPictures: string[];
    showImageSlider: boolean;
    showNavigation = false;

    leasingOptions$ = this.leasingService.getActiveLeasingToggles().pipe(
        map((a) => ({
            leasingPurpose: (a?.leasingPurpose as LeasingPurpose) ?? 'brugtbilprivat',
            leasingType: (a?.leasingType as LeasingAgreementType) ?? 'F',
        }))
    );

    lazyOptions: LazyOptions = {
        loadOnTransitionStart: true,
        checkInView: true,
        loadPrevNext: false,
    };

    overviewPageUrl$ = this.navigationService.isB2B$.pipe(
        withLatestFrom(this.settingsService.get()),
        map(([isB2b, s]) =>
            isB2b
                ? s.globalPages.b2bUsedCarsOverviewPage.url
                : s.globalPages.usedCarsOverviewPage.url
        )
    );

    @HostListener('mouseenter') onMouseEnter() {
        if (this.resizedPictures?.length === 1) {
            return;
        }

        if (!this.showNavigation) {
            this.showNavigation = true;
        }

        const timeout = setTimeout(() => {
            const buttons = this.componentRef.nativeElement.querySelectorAll('.swiper-button-next, .swiper-button-prev');
            this.unlistenToClicks();

            // Stop routerLink navigation
            buttons.forEach((button: any) => this.unlistenClicks.push(this.renderer.listen(button, 'click', (event) => event.stopPropagation())));
            clearTimeout(timeout);
        });
    }

    @HostListener('mouseleave') onMouseLeave() {
        this.unlistenToClicks();
        this.showNavigation = false;
    }

    constructor(
        private carDetailsService: CarDetailsService,
        private featureDetectionService: FeatureDetectionService,
        private leasingService: LeasingService,
        private navigationService: NavigationService,
        private renderer: Renderer2,
        private settingsService: SettingsService,
        private zone: NgZone,
        public filterService: FilterService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {}

    ngOnInit() {
        this.resizedPictures = this.product.pictures ? this.product.pictures.map((p: any) => getSizedBilinfoImage(p, this.sliderImageSize)) : [];
        this.currentImageIndex = 0;
        this.altText = `${this.product?.make?.value} ${this.product?.model?.value} ${this.product?.variant?.value}`;

        if (this.sliderImagesAmount) {
            this.resizedPictures = this.resizedPictures.slice(0, this.sliderImagesAmount);
        }

        //Car variant if less than 5 add decimal .0
        let motorData = this.product?.motor?.data;

        if (motorData === 0) {
            motorData = '';
        } else if (motorData <= 5) {
            motorData = motorData.toFixed(1);
        }
        this.carModelDataToDecimal = motorData;
    }

    ngAfterViewInit() {
        this.checkForScrollToComponent();
    }

    intersectInitComponent(intersection: any) {
        if (intersection.isIntersecting) {
            this.showImageSlider = true;
        } else {
            this.showImageSlider = false;
        }
    }

    onSlideChange(swiper: any) {
        this.zone.run(() => {
            this.currentImageIndex = swiper.realIndex;
        });
    }

    unlistenToClicks() {
        if (this.unlistenClicks.length > 0) {
            this.unlistenClicks.forEach((fn: () => void) => fn());
            this.unlistenClicks.length = 0;
        }
    }

    ngOnDestroy() {
        this.unlistenToClicks();
    }

    setCurrentYPos() {
        this.carDetailsService.setVisitedFilterPageYPos(window.scrollY);
    }

    private checkForScrollToComponent() {
        // Angular is configured to scroll to last known Y position on the browser "back" event.
        // But Angular being Angular, we ensure that the last known Y scroll position is reached
        if (this.enableScrollTo) {
            const carDetailsId$ = this.carDetailsService.getVisitedCarId();
            const carDetailsYPos$ = this.carDetailsService.getVisitedFilterPageYPos();

            const connectStream$ = combineLatest([carDetailsId$, carDetailsYPos$]);
            connectStream$
                .pipe(
                    map(([id, yPos]) => {
                        if (this.product.id && this.product.id === id) {
                            setTimeout(() => {
                                if (this.document) {
                                    this.document.documentElement.scrollTop = yPos;
                                }

                                this.carDetailsService.setVisitedCarId(undefined); // Reset to avoid scrolling more than once
                                this.carDetailsService.setVisitedFilterPageYPos(0);
                            }, 20);
                        }
                    }),
                    take(1)
                ).subscribe();
        }
    }
}
